/**
 * Toast / notification store (Vue 2)
 * -------------------------------------------------------------
 * Framework-agnostic reactive toast queue, ported from the React useToast
 * hook. Uses `Vue.observable` (requires Vue >= 2.6).
 *
 * The Vue export components (mentee onboarding, manage invites, pending
 * invites) all emit a `notify({ title, description, variant })` event but
 * leave the actual rendering to the host. This store + <ToastHost> are that
 * missing piece: wire `@notify="notify"` and drop <ToastHost> in once near the
 * app root.
 *
 * `variant`:
 *   - "default" ...... neutral confirmation (e.g. "Verification code sent")
 *   - "destructive" .. error (e.g. "Incorrect password")
 *
 * Usage:
 *   import { notify } from "./toastStore";
 *   notify({ title: "Incorrect password",
 *            description: "Double-check the invitation password and try again.",
 *            variant: "destructive" });
 */
import Vue from 'vue/dist/vue.esm'

const DEFAULT_DURATION = 5000;

function uid() {
  return `toast_${Math.random().toString(36).slice(2, 9)}${Date.now()
    .toString(36)
    .slice(-4)}`;
}

export const toastState = Vue.observable({
  toasts: [],
});

const timers = {};

/**
 * Queue a toast. Returns the toast id so callers can dismiss it early.
 * duration <= 0 keeps the toast until dismissed manually.
 */
export function notify(payload) {
  const toast = Object.assign(
  {
    id: uid(),
    title: "",
    description: "",
    variant: "default",
    duration: DEFAULT_DURATION,
  },
  payload
  );
  toastState.toasts = [...toastState.toasts, toast];
  if (toast.duration > 0) {
    timers[toast.id] = setTimeout(() => dismiss(toast.id), toast.duration);
  }
  return toast.id;
}

export function dismiss(id) {
  if (timers[id]) {
    clearTimeout(timers[id]);
    delete timers[id];
  }
  toastState.toasts = toastState.toasts.filter((t) => t.id !== id);
}

export function clearToasts() {
  Object.keys(timers).forEach((id) => clearTimeout(timers[id]));
  Object.keys(timers).forEach((id) => delete timers[id]);
  toastState.toasts = [];
}



// WEBPACK FOOTER //
// ./src/toastStore.js